/**
 * SetupFieldsDialog — "one more detail for <app>" (2026-08-14 WhatsApp WABA
 * incident; enriched help + account-type triage 2026-08-17; extracted from
 * IntegrationsPanel 2026-08-18, behavior unchanged).
 *
 * The toolkit's own catalog metadata drives the form — zero per-app code.
 * When the server sends `help`, the pathway chooser asks FIRST and fields
 * render only for a pathway that can actually proceed (showFields).
 */

import React from 'react';
import type { ConnectInitField, ConnectToolkitHelp } from '../ComposioClient';
import PanelModal from './PanelModal';
import panelTheme from '../panelTheme';

export interface SetupPromptState {
  slug: string;
  fields: ConnectInitField[];
  values: Record<string, string>;
  note?: string;
  submitting?: boolean;
  /** Account-type triage (2026-08-17): when present, the dialog opens with
   *  a chooser; fields render only for the chosen pathway when it can
   *  actually proceed (showFields). */
  help?: ConnectToolkitHelp;
  pathwayId?: string;
}

export interface SetupFieldsDialogProps {
  prompt: SetupPromptState;
  onChange: (next: SetupPromptState) => void;
  onSubmit: () => void;
  onCancel: () => void;
  /** Surfaces that open OVER the chat overlay must clear its z-index
   *  (BODY_PORTAL_Z_INDEX doctrine) — the Integrations page default is
   *  fine at the modal's own depth. */
  zIndex?: number;
}

export const SetupFieldsDialog: React.FC<SetupFieldsDialogProps> = ({
  prompt,
  onChange,
  onSubmit,
  onCancel,
  zIndex,
}) => {
  const pathway = prompt.help?.pathways.find((p) => p.id === prompt.pathwayId) || null;
  const showFields = !prompt.help || Boolean(pathway?.showFields);

  return (
    <PanelModal onClose={onCancel} ariaLabel={`Setup for ${prompt.slug}`} testId="setup-fields-dialog" {...(zIndex != null ? { zIndex } : {})}>
      <div style={{ fontWeight: 700, color: panelTheme.ink, fontSize: '1rem', textTransform: 'lowercase' }}>
        one more detail for {prompt.slug.replace(/_/g, ' ')}
      </div>
      {prompt.help && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 600, color: panelTheme.ink, fontSize: '0.85rem', textTransform: 'lowercase' }}>
            {prompt.help.prompt}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {prompt.help.pathways.map((p) => (
              <button
                key={p.id}
                onClick={() => onChange({ ...prompt, pathwayId: p.id, note: undefined })}
                data-testid={`setup-pathway-${p.id}`}
                style={{
                  padding: '7px 12px', borderRadius: 999, fontSize: '0.78rem', cursor: 'pointer', textTransform: 'lowercase',
                  border: prompt.pathwayId === p.id ? `1.5px solid ${panelTheme.ink}` : `1px solid ${panelTheme.borderSoft}`,
                  background: prompt.pathwayId === p.id ? panelTheme.ink : '#fff',
                  color: prompt.pathwayId === p.id ? '#fff' : panelTheme.inkMuted,
                  fontWeight: prompt.pathwayId === p.id ? 700 : 500,
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          {pathway && (
            <div
              data-testid={`setup-pathway-guidance-${pathway.id}`}
              style={{ color: panelTheme.inkMuted, fontSize: '0.8rem', marginTop: 10, lineHeight: 1.55, background: panelTheme.surface, border: `1px solid ${panelTheme.borderFaint}`, borderRadius: 8, padding: '9px 12px' }}
            >
              {pathway.guidance}
            </div>
          )}
        </div>
      )}
      {showFields && (
        <div style={{ color: panelTheme.inkMuted, fontSize: '0.82rem', marginTop: 6, lineHeight: 1.5 }}>
          this app needs the following before its sign-in can start — the tips below say exactly where to find each value.
        </div>
      )}
      {showFields && prompt.fields.map((f) => (
        <div key={f.name} style={{ marginTop: 14 }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: panelTheme.ink }}>{f.displayName}</label>
          {f.description && (
            <div style={{ color: panelTheme.inkFaint, fontSize: '0.76rem', margin: '3px 0 6px', lineHeight: 1.5 }}>{f.description}</div>
          )}
          {/* Deep link beats navigation instructions (2026-08-17 WhatsApp
              journey): open the exact page in a new tab. */}
          {f.helpUrl && (
            <a
              href={f.helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`setup-field-help-${f.name}`}
              style={{ display: 'inline-block', color: panelTheme.ink, fontSize: '0.76rem', fontWeight: 600, margin: '0 0 6px', textDecoration: 'underline', textTransform: 'lowercase' }}
            >
              {f.helpLabel || 'open the page where this value lives'} ↗
            </a>
          )}
          <input
            // A credential is masked and never offered to the browser's autofill or spellcheck (2026-10-08).
            {...(f.secret ? { type: 'password', autoComplete: 'off', spellCheck: false } : {})}
            value={prompt.values[f.name] || ''}
            onChange={(e) => onChange({ ...prompt, values: { ...prompt.values, [f.name]: e.target.value } })}
            style={{ width: '100%', padding: '9px 12px', border: `1px solid ${panelTheme.borderSoft}`, borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
            data-testid={`setup-field-${f.name}`}
          />
          {/* The branch generic find-it copy never covers: the user who
              doesn't HAVE the value at all (2026-08-17 WhatsApp journey —
              phone-app-only users own no WABA). */}
          {f.notFoundHint && (
            <div
              data-testid={`setup-field-notfound-${f.name}`}
              style={{ color: panelTheme.inkFaint, fontSize: '0.74rem', marginTop: 6, lineHeight: 1.5, background: panelTheme.surface, border: `1px solid ${panelTheme.borderFaint}`, borderRadius: 8, padding: '7px 10px' }}
            >
              {f.notFoundHint}
            </div>
          )}
        </div>
      ))}
      {prompt.note && (
        <div style={{ color: panelTheme.dangerSoft, fontSize: '0.8rem', marginTop: 10, lineHeight: 1.5 }}>{prompt.note}</div>
      )}
      <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center' }}>
        {showFields && (
          <button
            onClick={onSubmit}
            disabled={!!prompt.submitting}
            style={{ padding: '9px 18px', background: panelTheme.ink, color: '#fff', border: 'none', borderRadius: 999, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', textTransform: 'lowercase' }}
          >
            {prompt.submitting ? 'starting sign-in…' : 'continue to sign-in'}
          </button>
        )}
        <button
          onClick={onCancel}
          style={{ background: 'none', border: 'none', color: panelTheme.inkFaint, fontSize: '0.82rem', cursor: 'pointer', textTransform: 'lowercase' }}
        >
          cancel
        </button>
      </div>
    </PanelModal>
  );
};

export default SetupFieldsDialog;
